import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useParams } from "react-router-dom";

import DropdownWithObjects from "../../components/Dropdowns/DropdownWithObjects/DropdownWithObjects";
import { useNotify } from "../../context/NotificationContext";
import {
  createStudentTeacherTelegramLink,
  fetchStudentRoles,
  fetchStudentTeachers,
  submitCourseApplication,
} from "../../services/studentPortalService/studentPortalService";
import type {
  StudentCatalogCourse,
  StudentRoleOption,
  StudentTeacherDetails,
} from "../../services/studentPortalService/studentPortalService.types";
import { getServerErrorData } from "../../utils/httpError";
import { logger } from "../../utils/logger";
import StudentBreadcrumbs from "./StudentBreadcrumbs";
import {
  ApplicationActions,
  ApplicationCancelButton,
  ApplicationError,
  ApplicationField,
  ApplicationFieldGrid,
  ApplicationInput,
  ApplicationLabel,
  ApplicationModal,
  ApplicationOverlay as PlatformApplicationOverlay,
  ApplicationSub,
  ApplicationTitle,
  CatalogCourseCard,
  CourseActionButton,
  CourseActionSlot,
  CourseChip,
  CourseDescription,
  CourseFoot,
  CourseFootText,
  CourseList,
  CourseMain,
  CourseMeta,
  CoursePrice,
  CourseSide,
  CourseTitle,
  CourseTop,
  CoursesPageLead,
  CoursesPageShell,
  CoursesPageTitle,
  DemoLoadingState,
  EmptyCourses,
  EmptyCoursesText,
  EmptyCoursesTitle,
  QuietCourseLink,
} from "./studentCourses.styles";
import {
  DetailSectionMain,
  DetailSectionSide,
  DetailSectionText,
  DetailSectionTitle,
  TeacherCatalogBlock,
  TeacherCatalogTitle,
  TeacherDetailSection,
  TeacherDetailSurface,
  TelegramButton,
  TelegramLink,
} from "./StudentTeachersPage.styles";

type ApplicationForm = {
  role: string;
  name: string;
  surname: string;
  phone: string;
  email: string;
};

const money = (value: string | number) => `${Number(value).toLocaleString("ru-RU")} руб.`;

const pluralCourses = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "курс";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "курса";
  return "курсов";
};

const StudentCatalogPage: React.FC = () => {
  const { teacherId = "" } = useParams();
  const numericTeacherId = Number(teacherId);
  const [teacher, setTeacher] = useState<StudentTeacherDetails | null>(null);
  const [roles, setRoles] = useState<StudentRoleOption[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<StudentCatalogCourse | null>(null);
  const [appliedCourseIds, setAppliedCourseIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState<ApplicationForm>({
    role: "",
    name: Cookies.get("first_name") || "",
    surname: Cookies.get("last_name") || "",
    phone: "",
    email: Cookies.get("email") || "",
  });
  const roleFieldRef = useRef<HTMLDivElement>(null);
  const toast = useNotify();

  const loadTeacher = useCallback(async (showError = true) => {
    try {
      const teachers = await fetchStudentTeachers();
      setTeacher(teachers.find((item) => item.id === numericTeacherId) || null);
    } catch (error) {
      logger.error("Ошибка загрузки каталога преподавателя:", error);
      if (showError) {
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось загрузить каталог преподавателя",
          life: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [numericTeacherId, toast]);

  useEffect(() => {
    setLoading(true);
    void loadTeacher();
  }, [loadTeacher]);

  useEffect(() => {
    let active = true;
    fetchStudentRoles()
      .then((items) => active && setRoles(items))
      .catch((error) => {
        logger.error("Ошибка загрузки ролей для заявки:", error);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      void loadTeacher(false);
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [loadTeacher]);

  useEffect(() => {
    if (!selectedCourse) return undefined;
    roleFieldRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedCourse(null);
        setErrors({});
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [selectedCourse]);

  const roleOptions = useMemo(
    () => roles.map((role) => ({ label: role.name, value: String(role.id) })),
    [roles],
  );

  const updateField = (field: keyof ApplicationForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const closeApplication = () => {
    if (submitting) return;
    setSelectedCourse(null);
    setErrors({});
  };

  const focusRoleField = () => {
    roleFieldRef.current?.querySelector<HTMLInputElement>("input")?.focus();
  };

  const handleApply = async () => {
    if (!selectedCourse) return;
    const required: (keyof ApplicationForm)[] = ["role", "name", "surname", "phone", "email"];
    const missing = required.reduce<Record<string, string>>((acc, field) => {
      if (!form[field].trim()) acc[field] = "Это поле обязательно";
      return acc;
    }, {});
    if (Object.keys(missing).length > 0) {
      setErrors(missing);
      if (missing.role) focusRoleField();
      return;
    }

    setSubmitting(true);
    setErrors({});
    try {
      await submitCourseApplication({
        course: selectedCourse.id,
        role: form.role,
        name: form.name,
        surname: form.surname,
        phone: form.phone,
        email: form.email,
      });
      setAppliedCourseIds((current) => new Set(current).add(selectedCourse.id));
      toast.show({ severity: "success", summary: "Готово", detail: "Заявка отправлена преподавателю", life: 3000 });
      setSelectedCourse(null);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formatted: Record<string, string> = {};
        Object.entries(serverErrors).forEach(([key, value]) => {
          formatted[key] = String(value);
        });
        setErrors(formatted);
      } else {
        logger.error("Ошибка отправки заявки:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось отправить заявку", life: 3000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const connectTelegram = async () => {
    if (!teacher) return;
    setConnecting(true);
    const popup = window.open("about:blank", "_blank");
    try {
      const link = await createStudentTeacherTelegramLink(teacher.id);
      if (popup) {
        popup.location.href = link.url;
      } else {
        window.location.assign(link.url);
      }
    } catch (error) {
      popup?.close();
      logger.error("Ошибка подготовки Telegram-ссылки:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось открыть Telegram-бот преподавателя",
        life: 3000,
      });
    } finally {
      setConnecting(false);
    }
  };

  const renderCourse = (course: StudentCatalogCourse) => (
    <CatalogCourseCard key={course.id}>
      <CourseTop>
        <CourseMain>
          <CourseMeta>{course.category}</CourseMeta>
          <CourseTitle>{course.title}</CourseTitle>
          <CourseDescription>{course.description}</CourseDescription>
        </CourseMain>
        <CourseSide>
          <CoursePrice>{money(course.price)}</CoursePrice>
        </CourseSide>
      </CourseTop>
      <CourseFoot>
        {course.has_access && <CourseFootText>Доступ уже открыт</CourseFootText>}
        <CourseActionSlot>
          {course.has_access ? (
            <QuietCourseLink to={`/student/courses/${course.id}`}>Открыть курс</QuietCourseLink>
          ) : appliedCourseIds.has(course.id) ? (
            <CourseChip>Заявка отправлена</CourseChip>
          ) : (
            <CourseActionButton
              type="button"
              onClick={() => {
                setSelectedCourse(course);
                setErrors({});
              }}
            >
              Подать заявку
            </CourseActionButton>
          )}
        </CourseActionSlot>
      </CourseFoot>
    </CatalogCourseCard>
  );

  if (loading) {
    return (
      <CoursesPageShell>
        <DemoLoadingState>Загружаем каталог…</DemoLoadingState>
      </CoursesPageShell>
    );
  }

  if (!teacher) {
    return (
      <CoursesPageShell>
        <StudentBreadcrumbs
          items={[
            { label: "Каталог курсов", to: "/student/catalog" },
            { label: "Преподаватель" },
          ]}
        />
        <EmptyCourses>
          <EmptyCoursesTitle>Преподаватель не найден</EmptyCoursesTitle>
          <EmptyCoursesText>Возможно, его каталог больше не доступен в вашем кабинете.</EmptyCoursesText>
        </EmptyCourses>
      </CoursesPageShell>
    );
  }

  const telegram = teacher.telegram;
  const telegramTitle = !telegram.available
    ? "Telegram-бот не настроен"
    : telegram.connected
      ? "Telegram подключён"
      : "Telegram не подключён";
  const telegramText = telegram.connected && telegram.bot_username
    ? `@${telegram.bot_username}`
    : telegram.available
      ? "Получайте оценки, комментарии и уведомления от этого преподавателя в Telegram."
      : "Подключение появится после настройки Telegram-бота преподавателем.";

  return (
    <CoursesPageShell>
      <StudentBreadcrumbs
        items={[
          { label: "Каталог курсов", to: "/student/catalog" },
          { label: teacher.organization_name },
        ]}
      />
      <CoursesPageTitle>{teacher.organization_name}</CoursesPageTitle>
      <CoursesPageLead>
        {teacher.courses.length} {pluralCourses(teacher.courses.length)} в каталоге. Выберите курс или подайте заявку на обучение.
      </CoursesPageLead>

      <TeacherDetailSurface>
        <TeacherDetailSection>
          <DetailSectionMain>
            <DetailSectionTitle>Telegram</DetailSectionTitle>
            <DetailSectionText>
              <strong>{telegramTitle}</strong>
              <br />
              {telegramText}
            </DetailSectionText>
          </DetailSectionMain>
          <DetailSectionSide>
            {telegram.connected && telegram.open_url ? (
              <TelegramLink href={telegram.open_url} target="_blank" rel="noreferrer">
                Открыть бота
              </TelegramLink>
            ) : telegram.available ? (
              <TelegramButton type="button" loading={connecting} onClick={connectTelegram}>
                Подключить Telegram
              </TelegramButton>
            ) : null}
          </DetailSectionSide>
        </TeacherDetailSection>
      </TeacherDetailSurface>

      <TeacherCatalogBlock>
        <TeacherCatalogTitle>Курсы</TeacherCatalogTitle>
        {teacher.courses.length === 0 ? (
          <EmptyCourses>
            <EmptyCoursesTitle>У преподавателя пока нет курсов</EmptyCoursesTitle>
            <EmptyCoursesText>Вернитесь в каталог и выберите другого преподавателя.</EmptyCoursesText>
          </EmptyCourses>
        ) : (
          <CourseList>{teacher.courses.map(renderCourse)}</CourseList>
        )}
      </TeacherCatalogBlock>

      {selectedCourse && (
        <PlatformApplicationOverlay onMouseDown={(event) => event.target === event.currentTarget && closeApplication()}>
          <ApplicationModal role="dialog" aria-modal="true" aria-labelledby="student-application-title">
            <ApplicationTitle id="student-application-title">Заявка на курс</ApplicationTitle>
            <ApplicationSub>
              {selectedCourse.title} · {teacher.organization_name} · {money(selectedCourse.price)}
            </ApplicationSub>

            <ApplicationField ref={roleFieldRef}>
              <ApplicationLabel>Кто вы</ApplicationLabel>
              <DropdownWithObjects
                options={roleOptions}
                placeholder="Выберите роль"
                value={form.role}
                onChange={(value) => updateField("role", value)}
              />
              {errors.role && <ApplicationError>{errors.role}</ApplicationError>}
            </ApplicationField>

            <ApplicationFieldGrid>
              <ApplicationField>
                <ApplicationLabel htmlFor="student-application-name">Имя</ApplicationLabel>
                <ApplicationInput
                  id="student-application-name"
                  value={form.name}
                  onChange={(event) => updateField("name", event.target.value)}
                  aria-invalid={Boolean(errors.name)}
                />
                {errors.name && <ApplicationError>{errors.name}</ApplicationError>}
              </ApplicationField>
              <ApplicationField>
                <ApplicationLabel htmlFor="student-application-surname">Фамилия</ApplicationLabel>
                <ApplicationInput
                  id="student-application-surname"
                  value={form.surname}
                  onChange={(event) => updateField("surname", event.target.value)}
                  aria-invalid={Boolean(errors.surname)}
                />
                {errors.surname && <ApplicationError>{errors.surname}</ApplicationError>}
              </ApplicationField>
            </ApplicationFieldGrid>

            <ApplicationFieldGrid>
              <ApplicationField>
                <ApplicationLabel htmlFor="student-application-phone">Телефон</ApplicationLabel>
                <ApplicationInput
                  id="student-application-phone"
                  placeholder="+7"
                  value={form.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                  aria-invalid={Boolean(errors.phone)}
                />
                {errors.phone && <ApplicationError>{errors.phone}</ApplicationError>}
              </ApplicationField>
              <ApplicationField>
                <ApplicationLabel htmlFor="student-application-email">Почта</ApplicationLabel>
                <ApplicationInput
                  id="student-application-email"
                  type="email"
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  aria-invalid={Boolean(errors.email)}
                />
                {errors.email && <ApplicationError>{errors.email}</ApplicationError>}
              </ApplicationField>
            </ApplicationFieldGrid>

            {errors.detail && <ApplicationError>{errors.detail}</ApplicationError>}

            <ApplicationActions>
              <ApplicationCancelButton type="button" onClick={closeApplication} disabled={submitting}>
                Отмена
              </ApplicationCancelButton>
              <CourseActionButton type="button" loading={submitting} onClick={handleApply}>
                Отправить заявку
              </CourseActionButton>
            </ApplicationActions>
          </ApplicationModal>
        </PlatformApplicationOverlay>
      )}
    </CoursesPageShell>
  );
};

export default StudentCatalogPage;
